<script>
   /**************************************************************/
   /* DataLoader for PCA/SIMCA app                               */
   /* -------------------------                                  */
   /* Loads test set from CSV file and returns is main output.   */
   /* If PCA model is provided it also applied the model and     */
   /* returns object with PCA results. In this case it will      */
   /* also recalculate the result object if array with outliers  */
   /* has been changed                                           */
   /**************************************************************/


   import { parseCSV, app_pca_predict, parseCSVParams } from '../utils.js';
   import { FileSelector, Switch, Spinner, Select, Container, Button, TextInput } from 'svelte-controls-basic';
   import DatasetWidget from '../widgets/DatasetWidget.svelte';


   let {
      WASM,                             // WASM object to make predictions if model is available (optional)
      model = null,                     // object with PCA model (optional)
      outliers = [],                    // array with outlier indices (optional)
      className = "Class",              // name of class related fields in the widget (e.g. 'Class' or 'Group')
      classMandatory = false,           // logical, if class column is mandatory in the data file
      classUnique = false,              // logical, if class labels must be all the same
      title = '',                       // title, to show on top of the block
      dataset = $bindable(),            // JSON with datasets
      resobj = $bindable(),             // JSON with PCA results (if model is provided)
   } = $props();

   // possible delimeters and header options
   const sepOptions = [',', ';'];
   const headerOptions = $derived(model ? ['no', 'yes'] : ['no', 'labels', 'values']);

   // data properties
   let dataFile = $state();              // CSV file
   let varvaluesName = $state('');       // name of the variable values to be used as header
   let varvaluesUnits = $state('');      // units of the variable values

   // data properties, that will be autodetected from CSV file
   let sep = $state(',');                // seperator between values
   let header = $state('no');            // first row contains header (labels or values)
   let hasLabels = $state(false);        // first column contains row labels
   let hasClassLabels = $state(false);   // if data file contains class labels


   // promise, app status for handling and showing errors
   let promise = $state(null);
   let status = $state('neutral');
   let errMsg = $state('');


   // method to apply PCA model
   function predict(model, dataset, outliers) {
      promise = WASM()
         .then(Module => {
            resobj = app_pca_predict(Module, model, dataset, outliers);
         })
         .catch(e =>{
            console.error(e);
            resobj = null;
            status = 'error';
            errMsg = e;
         });
   };

   // method for guessing CSV parameters
   function guessParams(dataFile, model) {

      // no data file is provided - no guessing
      if (!dataFile || dataFile.size < 10) return;

      // read first 100Kb
      const chunkSize = 100000;
      const first100KB = dataFile.slice(0, chunkSize);

      const reader = new FileReader();
      reader.onload = () => {
         const csvString = reader.result;
         if (!csvString || csvString.length < 0) return;
         [sep, hasLabels, hasClassLabels, header] = parseCSVParams(csvString);
         hasClassLabels = (classMandatory || hasClassLabels);
         if (model && header !== 'no') header = 'yes';
      };

      reader.onerror = function(e) {
         csvString = null;
         status = 'error';
         errMsg = e;
         console.error('Error reading CSV file.', e);
      };

      reader.readAsText(first100KB);
   }

   // method for parsing CSV content provided as one large string to dataset object.
   function setData(strData) {
      try{
         const d = parseCSV(
            strData,
            sep,
            header === 'yes' ? 'labels' : header, // when load data for prediction simpler interface is used
            hasLabels,
            hasClassLabels,
            varvaluesName,
            varvaluesUnits
         );

         if (d.values.nrows < 1 ) {
            throw new Error('Dataset must have at least 1 row.');
         }

         if (model && d.values.ncols !== (model.ncols + model.exclvars.length)) {
            throw new Error('Dataset has different number of columns than the training set.');
         }

         if (d.classNames.length > d.values.nrows / 2) {
            throw new Error('Dataset has too many unique ' + className.toLowerCase() + ' labels (' + d.classNames.length + '), check your CSV file.');
         }

         if (classMandatory && d.classNames.length === 0) {
            throw new Error('Dataset set must have a column filled with class names.');
         }

         if (classUnique && d.classNames.length > 1) {
            throw new Error('Dataset set must have a column filled with only single (target) class name.');
         }

         d['fileName'] = dataFile.name.replace(/\.[^/.]+$/, "");

         // if model is available inherit variable properties from the model
         if (model) {
            // if data values in the training set were in reverse order it is expected
            // that they are in the reverse order in this dataset as well
            d.varrev = model.varrev;
            d.header = model.header;
            d.varlabels = model.varlabels;
            d.varvalues = model.varvalues;
            d.varvaluesName = model.varvaluesName;
            d.varvaluesUnits = model.varvaluesUnits;
         }

         dataset = d;
         errMsg = ''
         status = 'success';
      } catch (e) {
         console.error(e)
         dataset = null;
         status = 'error';
         errMsg = e;
      }
   }

   // promise for reading the CSV file
   function readFile (e) {
      const reader = new FileReader();
      reader.onload = () => { setData(reader.result) };
      reader.readAsText(dataFile);
   }

   // reset whole component to initial state of the component
   function reset () {
      dataFile = '';
      dataset = null;
      resobj = null;
      varvaluesName = '';
      varvaluesUnits = '';
      status = 'neutral';
      errMsg = '';
   }

   // if data file is selected - guess parameters
   $effect(() => {
      if (dataFile === null) {
         // if data file is empty reset status and error messages
         reset();
      } else {
         // if not just remove error messages if any and guess the parameters
         guessParams(dataFile, model);
         status = 'neutral';
         errMsg = '';
      }
   });

   // if separator was changed remove error message
   $effect(() => {
      if (sep) {
         status = 'neutral';
         errMsg = '';
      }
   });

   // if dataset, model or array with outliers have been changed, make new predictions
   $effect(() => {
      if (dataset && model && !resobj && !errMsg) {
         try{
            resobj = predict(model, dataset, outliers);
            errMsg = ''
            status = 'success';
         } catch (e) {
            console.error(e)
            dataset = null;
            resobj = null;
            status = 'error';
            errMsg = e;
         }
      }
   });
</script>

{#if !dataset }

   <!-- show if data is not loaded -->
   <!-- reset component if dataFile is changed inside -->
   {#key dataFile === undefined}
   <Container>
      <FileSelector
         id="dataFile"
         message="Select CSV file with dataset"
         bind:file={dataFile}
      />
   </Container>
   {/key}

   <!-- show if file is selected but data is not loaded -->
   {#if dataFile}
   <Container label="Delimiter">
      <Select id="sep" bind:value={sep} options={sepOptions} />
   </Container>
   <Container label="Row labels">
      <Switch id="hasLabels" bind:value={hasLabels} options={['no', 'yes']} />
   </Container>
   {#if !classMandatory}
   <Container label="{className} labels">
      <Switch id="hasLabels" bind:value={hasClassLabels} options={['no', 'yes']} />
   </Container>
   {/if}
   <Container label="Header">
      <Select id="header" bind:value={header} options={headerOptions} />
   </Container>
   {#if header==='values'}
   <Container label="Header name">
      <TextInput id="varvaluesName" bind:value={varvaluesName} placeholder="e.g. Wavelength" />
   </Container>
   <Container label="Header units">
      <TextInput id="varvaluesUnits" bind:value={varvaluesUnits} maxLength={10} placeholder="e.g. nm" />
   </Container>
   {/if}
   <Container label="&nbsp;">
      <Button text='Load' onclick={readFile}/>
   </Container>
   {/if}

{:else}


   {#if promise}

      <!-- calculation is in process -->
      {#await promise}
         <Container>
            <Spinner />
            <p>{model ? 'Loading data and apply model...' : 'Loading data...'}</p>
         </Container>

      {:catch error}
         <!-- error message if failed -->
         <Container status="error">
            <p>{error}</p>
         </Container>
      {/await}

   {/if}

{/if}

{#if dataset }
   <!-- dataset is loaded - show info and cancel button -->
   <DatasetWidget {dataset} {reset} {title} />
{/if}

<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}

